import React, { useState, useEffect } from 'react';
import './App.css';
import ClientTable from './components/ClientTable';
import ClientForm from './components/ClientForm';
import MessageConfig from './components/MessageConfig';
import Login from './components/Login';
import UserManagement from './components/UserManagement';
import ChatbotFlows from './components/ChatbotFlows';
import { authApi } from './services/api';
import { User } from './types';

type Section = 'clients' | 'new-client' | 'messages' | 'users' | 'chatbot';

const App: React.FC = () => {
  const [activeSection, setActiveSection] = useState<Section>('clients');
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await authApi.getMe();
        setIsAuthenticated(true);
        setUser(response.user);
      } catch (error) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setIsAuthenticated(false);
      }
    }
    setLoading(false);
  };

  const handleLogin = (token: string, userData: User) => {
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setUser(null);
  };

  const handleRefresh = (): void => {
    setRefreshKey(prev => prev + 1);
    setActiveSection('clients');
  };

  if (loading) {
    return <div className="loading">Cargando...</div>;
  }

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  const isAdmin = user?.role === 'admin';

  return (
    <div className="App">
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1>Gestor de Clientes</h1>
          <div className="user-info">
            <span>{user?.full_name || user?.username}</span>
            <span className="user-role">{user?.role}</span>
          </div>
        </div>
        <nav className="sidebar-nav">
          <button
            className={`nav-item ${activeSection === 'clients' ? 'active' : ''}`}
            onClick={() => setActiveSection('clients')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 11l3 3L22 4"></path>
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path>
            </svg>
            Lista de Clientes
          </button>
          <button
            className={`nav-item ${activeSection === 'new-client' ? 'active' : ''}`}
            onClick={() => setActiveSection('new-client')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            Nuevo Cliente
          </button>
          <button
            className={`nav-item ${activeSection === 'messages' ? 'active' : ''}`}
            onClick={() => setActiveSection('messages')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"></circle>
              <path d="M12 1v6m0 6v6M5.64 5.64l4.24 4.24m4.24 4.24l4.24 4.24M1 12h6m6 0h6M5.64 18.36l4.24-4.24m4.24-4.24l4.24-4.24"></path>
            </svg>
            Configurar Mensajes
          </button>
          <button
            className={`nav-item ${activeSection === 'chatbot' ? 'active' : ''}`}
            onClick={() => setActiveSection('chatbot')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
            </svg>
            Chatbot
          </button>
          {isAdmin && (
            <button
              className={`nav-item ${activeSection === 'users' ? 'active' : ''}`}
              onClick={() => setActiveSection('users')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
              Usuarios
            </button>
          )}
          <button className="nav-item logout" onClick={handleLogout}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
            Cerrar Sesión
          </button>
        </nav>
      </aside>

      <main className="App-main">
        {activeSection === 'clients' && (
          <div className="section-container">
            <h2 className="section-title">Lista de Clientes</h2>
            <ClientTable key={refreshKey} />
          </div>
        )}
        {activeSection === 'new-client' && (
          <div className="section-container">
            <h2 className="section-title">Nuevo Cliente</h2>
            <ClientForm
              onSuccess={handleRefresh}
              onCancel={() => setActiveSection('clients')}
            />
          </div>
        )}
        {activeSection === 'messages' && (
          <div className="section-container">
            <h2 className="section-title">Configurar Mensajes</h2>
            <MessageConfig />
          </div>
        )}
        {activeSection === 'chatbot' && (
          <div className="section-container">
            <h2 className="section-title">Gestión de Flujos de Chatbot</h2>
            <ChatbotFlows />
          </div>
        )}
        {activeSection === 'users' && isAdmin && (
          <div className="section-container">
            <h2 className="section-title">Gestión de Usuarios</h2>
            <UserManagement />
          </div>
        )}
      </main>
    </div>
  );
};

export default App;
